import { useState, useEffect, useCallback } from 'react'
import { ThumbsUp, ThumbsDown } from 'lucide-react'
import { getContentFeedback, submitContentFeedback } from '../../lib/api'

interface FeedbackButtonsProps {
  contentType: string
  slug: string
}

export function FeedbackButtons({ contentType, slug }: FeedbackButtonsProps) {
  const [vote, setVote] = useState<boolean | null>(null)
  const [counts, setCounts] = useState({ up: 0, down: 0 })
  const [loading, setLoading] = useState(false)

  // Fetch existing feedback on mount and when slug changes
  useEffect(() => {
    let cancelled = false
    setVote(null)
    setCounts({ up: 0, down: 0 })

    getContentFeedback(contentType, slug)
      .then((data) => {
        if (!cancelled) {
          setVote(data.user_vote)
          setCounts({ up: data.up_count, down: data.down_count })
        }
      })
      .catch((err) => {
        console.error('Failed to load feedback:', err)
      })

    return () => {
      cancelled = true
    }
  }, [contentType, slug])

  const handleVote = useCallback(
    async (isPositive: boolean) => {
      if (loading) return

      // If clicking the same vote, it's a no-op
      if (vote === isPositive) return

      // Optimistic update
      const prevVote = vote
      const prevCounts = { ...counts }

      const newCounts = { ...counts }
      // Remove previous vote effect
      if (prevVote === true) newCounts.up--
      if (prevVote === false) newCounts.down--
      // Apply new vote
      if (isPositive) newCounts.up++
      else newCounts.down++

      setVote(isPositive)
      setCounts(newCounts)
      setLoading(true)

      try {
        await submitContentFeedback(contentType, slug, isPositive)
      } catch (err) {
        console.error('Failed to submit feedback:', err)
        // Revert on error
        setVote(prevVote)
        setCounts(prevCounts)
      } finally {
        setLoading(false)
      }
    },
    [contentType, slug, vote, counts, loading]
  )

  return (
    <div className="flex items-center gap-4 mt-8 pt-4 border-t border-border">
      <span className="text-sm text-muted-foreground">Was this helpful?</span>
      <button
        onClick={() => handleVote(true)}
        disabled={loading}
        className={`p-2 rounded-lg transition-colors ${
          vote === true
            ? 'text-green-400 bg-green-500/10'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
        aria-label="Thumbs up"
      >
        <ThumbsUp className="w-4 h-4" />
      </button>
      {counts.up > 0 && (
        <span className="text-xs text-muted-foreground -ml-2">{counts.up}</span>
      )}
      <button
        onClick={() => handleVote(false)}
        disabled={loading}
        className={`p-2 rounded-lg transition-colors ${
          vote === false
            ? 'text-red-400 bg-red-500/10'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
        aria-label="Thumbs down"
      >
        <ThumbsDown className="w-4 h-4" />
      </button>
      {counts.down > 0 && (
        <span className="text-xs text-muted-foreground -ml-2">{counts.down}</span>
      )}
    </div>
  )
}
